"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const RetailPage = () => {
    const { t, translationsLoaded } = useComponentTranslations('Retail');
    const rt = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: rt('retailExpertise1'), icon: 'carbon:analytics' },
        { title: rt('retailExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: rt('retailExpertise3'), icon: 'carbon:rule' },
        { title: rt('retailExpertise4'), icon: 'carbon:security' },
        { title: rt('retailExpertise5'), icon: 'carbon:cloud-app' },
        { title: rt('retailExpertise6'), icon: 'carbon:blockchain' },
        { title: rt('retailExpertise7'), icon: 'carbon:data-refinery' },
        { title: rt('retailExpertise8'), icon: 'carbon:chart-line-data' },
        { title: rt('retailExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: rt('retailUseCase1'), description: rt('retailUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: rt('retailUseCase2'), description: rt('retailUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: rt('retailUseCase3'), description: rt('retailUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: rt('retailUseCase4'), description: rt('retailUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: rt('retailService1Title'), description: rt('retailService1Desc'), image: '/images/finance.jpg' },
        { title: rt('retailService2Title'), description: rt('retailService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: rt('retailService3Title'), description: rt('retailService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: rt('retailService4Title'), description: rt('retailService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: rt('retailService5Title'), description: rt('retailService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: rt('retailFaq1q'), answer: rt('retailFaq1a') },
        { question: rt('retailFaq2q'), answer: rt('retailFaq2a') },
        { question: rt('retailFaq3q'), answer: rt('retailFaq3a') },
        { question: rt('retailFaq4q'), answer: rt('retailFaq4a') },
        { question: rt('retailFaq5q'), answer: rt('retailFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {rt('retailHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {rt('retailHeroSubtitle')}
                </motion.p>
            </header>

            <section className="w-full py-24 glass-section glass-border-y">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{rt('retailPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {rt('retailPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{rt('retailExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                style={{ willChange: "transform, opacity" }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={rt('retailServicesTitle')} useCases={servicesForAlternating} />
            </div>

            <ServicesAccordion title={rt('retailUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={rt('retailTechTitle')} t={t} />
                <IndustrialsForm label={rt('retailCtaTitle')} />

                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{rt('retailFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 glass-border-strong glass-rounded glass-card">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
};

export default RetailPage;
